import React, { useMemo } from 'react';
import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '../../legacy-components/tooltip';

// TODO Add theme and size instead of using className for theming so everyone
//      gets consistent style.
export type CopyToClipboardButtonProps = {
  text: string;
  className?: string;
  feedbackPlacement?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * The delay in milliseconds before the "Copied!" feedback disappears.
   */
  feedbackDelay?: number;
  tooltipText?: string;
  tooltipTextOnCopy?: string;
  icon?: React.ReactNode;
};

export function CopyToClipboardButton({
  icon,
  text,
  className = 'h-5 w-5 text-darker-gray',
  feedbackPlacement = 'left',
  feedbackDelay = 2500,
  tooltipText = 'Copy to clipboard',
  tooltipTextOnCopy = 'Copied!',
}: CopyToClipboardButtonProps) {
  const [isCopied, setIsCopied] = React.useState(false);
  const copyToClipboard = React.useCallback(() => {
    if (isCopied) {
      return;
    }
    let timeoutId: ReturnType<typeof setTimeout>;
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      timeoutId = setTimeout(() => setIsCopied(false), feedbackDelay);
    });
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [text, isCopied, feedbackDelay]);

  const feedbackPlacementClassName = useMemo(() => {
    switch (feedbackPlacement) {
      case 'top':
        return 'absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full -mt-2';
      case 'right':
        return 'absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-full -mr-2';
      case 'bottom':
        return 'absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full -mb-2';
      case 'left':
        return 'absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full -ml-2';
    }
  }, [feedbackPlacement]);

  return (
    <button className={className} onClick={copyToClipboard}>
      {isCopied ? (
        <div className="relative h-full w-full">
          <CheckIcon className="text-positive-base" strokeWidth={3} />
          <div
            className={
              'absolute z-[101] rounded-md bg-gray-700 p-1.5 text-xs text-white ' +
              feedbackPlacementClassName
            }
          >
            {tooltipTextOnCopy}
          </div>
        </div>
      ) : (
        <Tooltip label={tooltipText}>
          {icon ? (
            <>{icon}</>
          ) : (
            <DocumentDuplicateIcon className="h-5 w-5" strokeWidth={2} />
          )}
        </Tooltip>
      )}
    </button>
  );
}
